import { createContext, type Dispatch } from 'react'

import { IconLoader, IconNote } from '@tabler/icons-react'
import type { JSONContent } from '@tiptap/react'
import type {
  Assistant,
  AssistantTool,
  AssistantToolChoice,
  CRMObject,
  ObjectAuthorization,
  Page,
  Thread,
} from 'types/graphql'
import type { ThreadMessage } from 'types/graphql'

export type ToolResultMessage = Partial<ThreadMessage> &
  Required<Pick<ThreadMessage, 'type'>>

export const LoadingMessages = {
  CREATING_THREAD: {
    label: 'Creating thread...',
    icon: IconLoader,
  },
  ADDING_MESSAGE: {
    label: 'Adding message...',
    icon: IconLoader,
  },
  CREATING_PAGE: {
    label: 'Creating page...',
    icon: IconNote,
  },
  AI_THINKING: {
    label: 'AI is thinking...',
    icon: IconLoader,
  },
}

export enum ContextStatus {
  LOADING = 'loading',
  ERROR_CAPACITY = 'error_capacity',
  ERROR_UNKNOWN = 'error_unknown',
  SUCCESS = 'loaded',
}

export type ThreadMachineState =
  | 'LoadingThread'
  | 'Idle'
  | 'Error'
  | 'Loading'
  | 'StreamReady'
  | 'WaitingForStream'
  | 'Streaming'
  | 'WaitingForTool'
  | 'CreatingThread'

export interface ToolPreview {
  id: string
  name: string
  status: 'preparing' | 'ready'
  arguments?: any
  detectedAt: number
  toolUuid: string
}

export interface ThreadState {
  currentState: ThreadMachineState

  id: string | null
  workspaceId: string | null
  thread: Thread | null
  threads: Thread[]
  pendingAiPrompt: string | null
  loadingMessage: {
    label: string
    icon?: typeof IconLoader
  } | null
  errorMessage: string | null
  statusMessage: string | null
  contextStatus: ContextStatus | null
  pages: Page[]
  contextString: string | null
  contextObjects: Partial<CRMObject>[] | null
  dismissedObjects: Partial<CRMObject>[] | null
  token: string | null
  tokenCount: number
  tokenLimit: number | null
  modelId: string | null
  thinkingEnabled: boolean
  instructions: string | null

  aiInitializedAt: number | null
  contentPopulatedAt: number | null
  currentStreamedContent: string
  lastChunkReceivedAt: number | null
  isContextUpdatePending: boolean
  localMessages: string[]
  availableTools: AssistantTool[]
  enabledTools: AssistantTool[]
  needsThreadRefetch: boolean
  pendingToolCalls: AssistantToolChoice[] | null
  pendingServerToolCalls: (AssistantToolChoice & { toolUuid: string })[] | null

  debugString: string | null
  useDebug: boolean
  navOpen: boolean
}

export const initialThreadState: ThreadState = {
  currentState: 'LoadingThread',
  id: null,
  workspaceId: null,
  thread: null,
  threads: [],
  pendingAiPrompt: null,
  loadingMessage: { label: 'Loading Thread...' },
  errorMessage: null,
  statusMessage: null,
  contextStatus: null,
  pages: [],
  contextString: null,
  contextObjects: null,
  dismissedObjects: null,
  token: null,
  tokenCount: 0,
  aiInitializedAt: null,
  contentPopulatedAt: null,
  currentStreamedContent: '',
  modelId: null,
  tokenLimit: null,
  thinkingEnabled: false,
  lastChunkReceivedAt: null,
  isContextUpdatePending: false,
  localMessages: [],
  availableTools: [],
  enabledTools: [],
  instructions: '',
  debugString: '',
  useDebug: false,
  navOpen: false,
  needsThreadRefetch: false,
  pendingToolCalls: null,
  pendingServerToolCalls: null,
}

export type ThreadAction =
  | { type: 'SET_THREAD'; payload: Thread }
  | { type: 'ADD_MESSAGE'; payload: ThreadMessage }
  | {
      type: 'SET_CONTEXT_OBJECTS'
      payload: {
        contextObjects?: Partial<CRMObject>[]
        dismissedObjects?: Partial<CRMObject>[]
      }
    }
  | { type: 'SET_CONTEXT_STATUS'; payload: ContextStatus }
  | { type: 'SET_CONTEXT_STRING'; payload: string }
  | { type: 'SET_THREAD_ERROR'; payload: string }
  | { type: 'SET_THREAD_STATUS'; payload: string }
  | { type: 'SET_THREADS'; payload: Thread[] }
  | { type: 'TOGGLE_DEBUG' }
  | {
      type: 'SET_PENDING_AI_PROMPT'
      payload: {
        questionHtml?: string | null
        questionJson?: JSONContent | null
        question?: string | null
        userId: string
        skipAddMessage?: boolean
      }
    }
  | {
      type: 'SET_LOADING_MESSAGE'
      payload: { label: string; icon?: typeof IconLoader } | null
    }
  | { type: 'SET_TOKEN_COUNT'; payload: number }
  | { type: 'SET_TOKEN'; payload: string }
  | { type: 'RESET_THREAD' }
  | { type: 'SET_THREAD_TITLE'; payload: string }
  | { type: 'USER_CANCELLED' }
  | {
      type: 'SET_AI_INITIALIZED_AT'
      payload?: { timestamp?: number }
    }
  | {
      type: 'STREAM_CHUNK_RECEIVED'
      payload: { timestamp?: number; chunk?: string }
    }
  | {
      type: 'STREAM_ENDED'
      payload?: {
        html?: string
        json?: JSONContent
      }
    }
  | {
      type: 'STREAMING_TIMEOUT'
      payload?: { type: 'start' | 'during' }
    }
  | {
      type: 'TOOL_EXECUTION_ERROR'
      payload: { results: ToolResultMessage[] }
    }
  | { type: 'CONTEXT_UPDATE_STARTED' }
  | { type: 'SET_MODEL_ID'; payload: { modelId: string } }
  | {
      type: 'MODEL_CHANGED'
      payload: {
        oldModelId: string
        newModelId: string
        reason: string
      }
    }
  | {
      type: 'SAVED_MESSAGES_TO_DB'
      payload: {
        localToDbIds: Record<string, string>
        persistedLocalIds: string[]
      }
    }
  | { type: 'START_NEW_THREAD'; payload: { workspaceId: string } }
  | { type: 'SET_AVAILABLE_TOOLS'; payload: { tools: AssistantTool[] } }
  | { type: 'SET_ENABLED_TOOLS'; payload: { tools: AssistantTool[] } }
  | { type: 'SET_ASSISTANT_GOAL'; payload: string | null }
  | {
      type: 'ADD_CONTEXT_OBJECTS'
      payload: { contextObjects: Partial<CRMObject>[] }
    }
  | {
      type: 'UPDATE_CONTEXT_OBJECTS'
      payload: {
        contextObjects: Partial<CRMObject>[]
        context: { contextString: string; tokenCount: number }
      }
    }
  | {
      type: 'CONTEXT_UPDATE_RECEIVED'
      payload: { contextString: string; tokenCount: number }
    }
  | {
      type: 'THREAD_LOADED'
      payload: {
        thread: Thread
        context: { contextString: string; tokenCount: number }
        tools: AssistantTool[]
        instructions: string | null
      }
    }
  | { type: 'SET_NAV_OPEN'; payload: boolean }
  | {
      type: 'TOOL_CALL_DETECTED'
      payload: {
        id: string
        name: string
        index: number
        toolUuid: string
        currentContent?: string
      }
    }
  | {
      type: 'TOOL_RESPONSE_RECEIVED'
      payload: {
        results: ToolResultMessage[]
      }
    }
  | {
      type: 'TOOL_DETAILS_RECEIVED'
      payload: {
        toolCalls: (AssistantToolChoice & { toolUuid: string })[]
      }
    }
  | {
      type: 'TOOL_CALL_FAILED'
      payload: {
        toolUuid: string
        toolName: string
        error: string
      }
    }
  | {
      type: 'ADD_SYSTEM_MESSAGE'
      payload: {
        message: string
      }
    }
  | {
      type: 'SET_PENDING_TOOL_CALLS'
      payload: {
        toolCalls: (AssistantToolChoice & { toolUuid: string })[]
      }
    }
  | {
      type: 'SET_PENDING_SERVER_TOOL_CALLS'
      payload: {
        toolCalls: (AssistantToolChoice & { toolUuid: string })[]
      }
    }

export type ThreadContextValue = {
  state: ThreadState
  dispatch: Dispatch<ThreadAction>

  fetchContext: () => void
  getThreadMessage: (threadMessageId: string) => void

  updateContextObjects: ({
    contextObjects,
    dismissedObjects,
  }: {
    contextObjects: Partial<CRMObject>[]
    dismissedObjects: Partial<CRMObject>[]
  }) => void
  updateTitle: (title: string) => void
  handleAiResponse: (response: string) => void
  assistant: Assistant | null
  onModelUpdate?: (modelId: string) => void
  refetchThread?: () => void
  authorization: ObjectAuthorization | null
}

const ThreadContext = createContext<ThreadContextValue | null>(null)

export default ThreadContext
